import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Building2, Loader2 } from 'lucide-react'
import { orgAPI } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useOrg } from '../context/OrgContext'
import { PENDING_ORG_INVITE_TOKEN_KEY } from '../constants/orgInvite'

/**
 * Public page: org invitation preview + accept/decline (auth required for actions).
 * Preserves ?token= through signup/login links.
 */
function JoinPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')?.trim() ?? ''
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { refetchOrg } = useOrg()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [preview, setPreview] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)

  const loadPreview = useCallback(async () => {
    if (!token) {
      setError('Missing invitation link. Use the link from your email.')
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    try {
      const res = await orgAPI.previewInvitation(token)
      const data = res?.data ?? res
      setPreview(data ?? null)
    } catch (e) {
      setError(e?.message || 'Could not load this invitation.')
      setPreview(null)
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    loadPreview()
  }, [loadPreview])

  const status = preview?.inviteStatus ?? preview?.status ?? ''
  const isPending = status === 'pending'
  const loginHref = `/login?token=${encodeURIComponent(token)}`
  const signupHref = `/signup?token=${encodeURIComponent(token)}`

  const handleAccept = async () => {
    if (!token || !isAuthenticated) return
    setActionLoading(true)
    setError('')
    try {
      await orgAPI.acceptInvitation(token)
      try {
        localStorage.removeItem(PENDING_ORG_INVITE_TOKEN_KEY)
      } catch {
        /* ignore */
      }
      await refetchOrg()
      navigate('/dashboard', { replace: true })
    } catch (e) {
      setError(e?.message || 'Could not accept the invitation.')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDecline = async () => {
    if (!token || !isAuthenticated) return
    setActionLoading(true)
    setError('')
    try {
      await orgAPI.declineInvitation(token)
      try {
        localStorage.removeItem(PENDING_ORG_INVITE_TOKEN_KEY)
      } catch {
        /* ignore */
      }
      navigate('/dashboard', { replace: true })
    } catch (e) {
      setError(e?.message || 'Could not decline the invitation.')
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-teal-50/40 px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-8 shadow-sm"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-indigo-700 flex items-center justify-center">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Organization invitation</h1>
            <p className="text-sm text-gray-500">Review before you join</p>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading invitation…
          </div>
        )}

        {!loading && error && !preview && (
          <p className="text-sm text-red-600 mb-4">{error}</p>
        )}

        {!loading && preview && (
          <>
            {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

            <div className="space-y-3 text-gray-700 mb-6">
              <p>
                <span className="font-medium text-gray-900">{preview.inviterName || 'Someone'}</span>
                {' invited you to join '}
                <span className="font-semibold text-gray-900">{preview.orgName || 'an organization'}</span>
                {' on Rithmo'}
                {preview.role ? (
                  <>
                    {' as '}
                    <span className="capitalize">{preview.role.replace(/_/g, ' ')}</span>
                  </>
                ) : null}
                .
              </p>
              {preview.expiresAt && isPending && (
                <p className="text-xs text-gray-500">
                  Expires {new Date(preview.expiresAt).toLocaleString()}
                </p>
              )}
              {!isPending && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  This invitation is no longer active ({status}).
                </p>
              )}
            </div>

            <div className="rounded-lg bg-slate-50 border border-slate-100 px-4 py-3 text-sm text-slate-600 mb-6">
              By accepting, you join this organization in Rithmo. Organization admins may be able to see
              meetings and content shared in that organization, consistent with your settings and policies.
            </div>

            {!isAuthenticated && isPending && (
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to={signupHref} className="btn-primary text-center">
                  Create account
                </Link>
                <Link to={loginHref} className="btn-secondary text-center">
                  Sign in
                </Link>
              </div>
            )}

            {isAuthenticated && isPending && (
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleAccept}
                  disabled={actionLoading}
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Accept
                </button>
                <button
                  type="button"
                  onClick={handleDecline}
                  disabled={actionLoading}
                  className="btn-secondary"
                >
                  Decline
                </button>
              </div>
            )}

            <p className="text-xs text-gray-400 mt-8 text-center">
              <Link to="/" className="underline hover:text-gray-600">
                Back to home
              </Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  )
}

export default JoinPage
